import React, { useEffect, useMemo, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { SpeechSegment } from '../../../services/api.service';
import { highlightSearch } from '../../../utils/tech-terms';
import { segmentMatchesThemeWords } from '../utils/meeting-analytics';
import { formatSpeakerWithLang, langLabel } from '../utils/speaker-language';
import { getSegmentStartSec, getSpeakerName } from '../utils/audio-segments';
import { canonicalizeSpeakerDisplayName } from '../../../utils/speaker-display-names';
import { resolveSpeakerRole } from '../../../utils/speaker-roles';
import SpeakerRoleBadge from './speaker-role-badge.component';
import { TaggedReplica } from '../../../services/api.service';

const cn = createCn('file', classes);

const SPEAKER_COLORS = ['#1B80FC', '#00B894', '#FF428A', '#F59E0B', '#6C5CE7', '#E17055'];

function dedupeConsecutiveSegments(segs: SpeechSegment[]): SpeechSegment[] {
  const out: SpeechSegment[] = [];
  for (const seg of segs) {
    const prev = out[out.length - 1];
    if (!prev) {
      out.push(seg);
      continue;
    }
    const prevBase = getSpeakerName(prev);
    const base = getSpeakerName(seg);
    if (prevBase !== base) {
      out.push(seg);
      continue;
    }
    const t1 = (prev.text || '').trim().toLowerCase();
    const t2 = (seg.text || '').trim().toLowerCase();
    const dt = Math.abs(getSegmentStartSec(seg) - getSegmentStartSec(prev));
    if (t1 && t1 === t2 && dt < 25) continue;
    if (dt < 15 && t1.length < 60 && t2.length < 60 && t1 && t2 && (t1.includes(t2) || t2.includes(t1))) {
      continue;
    }
    out.push(seg);
  }
  return out;
}

type Props = {
  segments: SpeechSegment[];
  fallbackText?: string;
  duration?: string;
  onSeekTime?: (seconds: number) => void;
  onSpeakerPlay?: (speakerName: string) => void;
  soloSpeakerName?: string | null;
  externalSearch?: string;
  externalSearchTerms?: string[];
  onExternalSearchChange?: (value: string) => void;
  taggedReplicas?: TaggedReplica[];
};

function toArray(segments: SpeechSegment[] | unknown): SpeechSegment[] {
  if (Array.isArray(segments)) return segments;
  return [];
}

function parseTimeToSeconds(time: string): number {
  if (!time) return 0;
  const parts = time.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

function formatTimeShort(time: string, startSeconds?: number): string {
  const sec = typeof startSeconds === 'number' && Number.isFinite(startSeconds)
    ? startSeconds
    : parseTimeToSeconds(time);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function speakerInitials(name: string): string {
  const base = canonicalizeSpeakerDisplayName(
    name
      .replace(/\s*\((RU|KZ|EN|RU\+KZ|\?)\)\s*/gi, ' ')
      .replace(/\s*·\s*[^·]+$/g, '')
      .trim(),
  );
  const parts = base.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0];
  if (/^SPEAKER_\d+$/i.test(first)) {
    const m = first.match(/SPEAKER_(\d+)/i);
    return m ? `S${parseInt(m[1], 10)}` : first.slice(0, 2).toUpperCase();
  }
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const TranscriptionView = ({
  segments: rawSegments,
  fallbackText,
  duration,
  onSeekTime,
  onSpeakerPlay,
  soloSpeakerName = null,
  externalSearch,
  externalSearchTerms,
  onExternalSearchChange,
  taggedReplicas = [],
}: Props) => {
  const segments = toArray(rawSegments);
  const [search, setSearch] = useState(externalSearch || '');
  const [activeSpeaker, setActiveSpeaker] = useState<string | null>(null);

  useEffect(() => {
    if (externalSearch !== undefined) {
      setSearch(externalSearch);
    }
  }, [externalSearch]);

  const { speakerColors, speakerStats, items } = useMemo(() => {
    const colors: Record<string, string> = {};
    const stats: Record<string, { count: number; minutes: number; lang?: string }> = {};
    let colorIdx = 0;

    const valid = dedupeConsecutiveSegments(segments.filter(s => {
      const isKz = s.speaker_language === 'kz' || !!s.text_kk?.trim();
      const kkText = (s.text_kk || (isKz ? s.text : '') || '').trim();
      const ruText = (s.text_ru || (!isKz ? s.text : '') || '').trim();
      const primary = isKz ? (kkText || ruText) : (ruText || s.text);
      return primary?.trim();
    }));

    valid.forEach(seg => {
      const base = getSpeakerName(seg);
      const name = formatSpeakerWithLang(base, seg.speaker_language);
      const segLang = seg.speaker_language || seg.detected_language;
      if (!colors[name]) {
        colors[name] = SPEAKER_COLORS[colorIdx % SPEAKER_COLORS.length];
        colorIdx++;
      }
      const dur = seg.duration_seconds || Math.max(
        1,
        parseTimeToSeconds(seg.end_time) - parseTimeToSeconds(seg.start_time),
      );
      if (!stats[name]) stats[name] = { count: 0, minutes: 0, lang: segLang };
      stats[name].count += 1;
      stats[name].minutes += dur / 60;
      if (segLang && !stats[name].lang) stats[name].lang = segLang;
    });

    return {
      speakerColors: colors,
      speakerStats: Object.entries(stats)
        .map(([name, s]) => ({
          name,
          count: s.count,
          minutes: Math.round(s.minutes * 10) / 10,
          lang: s.lang,
        }))
        .sort((a, b) => b.minutes - a.minutes),
      items: valid,
    };
  }, [segments]);

  const tagBySecond = useMemo(() => {
    const map = new Map<number, string[]>();
    taggedReplicas.forEach(tr => {
      const sec = Math.round(tr.timestamp_sec || 0);
      const existing = map.get(sec) || [];
      tr.tags.forEach(t => {
        if (!existing.includes(t)) existing.push(t);
      });
      map.set(sec, existing);
    });
    return map;
  }, [taggedReplicas]);

  const findTagsForSegment = (startSec: number): string[] => {
    const rounded = Math.round(startSec);
    for (const delta of [0, 1, -1, 2, -2, 3, -3]) {
      const tags = tagBySecond.get(rounded + delta);
      if (tags?.length) return tags;
    }
    return [];
  };

  const activeTerms = useMemo(() => {
    if (externalSearchTerms?.length) return externalSearchTerms;
    const q = search.trim();
    if (!q) return [];
    return q.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
  }, [externalSearchTerms, search]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(seg => {
      const base = getSpeakerName(seg);
      const speaker = formatSpeakerWithLang(base, seg.speaker_language);
      if (activeSpeaker && speaker !== activeSpeaker) return false;
      if (activeTerms.length > 1 || externalSearchTerms?.length) {
        return segmentMatchesThemeWords(seg.text || seg.text_ru || '', activeTerms)
          || segmentMatchesThemeWords(seg.text_kk || '', activeTerms)
          || activeTerms.some(term => speaker.toLowerCase().includes(term.toLowerCase()));
      }
      if (!q) return true;
      return (
        seg.text.toLowerCase().includes(q) ||
        (seg.text_ru || '').toLowerCase().includes(q) ||
        (seg.text_kk || '').toLowerCase().includes(q) ||
        speaker.toLowerCase().includes(q)
      );
    });
  }, [items, search, activeSpeaker, activeTerms, externalSearchTerms]);

  const handleSeek = (seg: SpeechSegment) => {
    if (!onSeekTime) return;
    onSeekTime(getSegmentStartSec(seg));
  };

  if (!items.length) {
    if (fallbackText?.trim()) {
      return (
        <div className={cn('document', { transcription: true })}>
          <pre className={cn('transcription-fallback')}>{fallbackText}</pre>
        </div>
      );
    }
    return <p className={cn('empty')}>Транскрипция пуста.</p>;
  }

  return (
    <div className={cn('transcription-panel')}>
      <div className={cn('transcription-toolbar')}>
        <input
          type="search"
          className={cn('transcription-search')}
          placeholder="Поиск по тексту или спикеру…"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            onExternalSearchChange?.(e.target.value);
          }}
        />
        <span className={cn('transcription-meta')}>
          {filteredItems.length} / {items.length} реплик
          {duration ? ` · ${duration}` : ''}
        </span>
      </div>

      {speakerStats.length > 0 && (
        <div className={cn('transcription-stats')}>
          <table className={cn('transcription-stats-table')}>
            <thead>
              <tr>
                <th>Участник</th>
                <th>Роль</th>
                <th>Язык</th>
                <th>Реплик</th>
                <th>Время речи (мин)</th>
              </tr>
            </thead>
            <tbody>
              {speakerStats.map(row => {
                const resolved = resolveSpeakerRole(row.name);
                const color = speakerColors[row.name] || SPEAKER_COLORS[0];
                const isActive = activeSpeaker === row.name;
                const isSolo = soloSpeakerName != null && (
                  soloSpeakerName === row.name
                  || row.name.toLowerCase().includes(soloSpeakerName.toLowerCase())
                );
                return (
                  <tr
                    key={row.name}
                    className={cn('transcription-stats-row', { active: isActive || isSolo })}
                    onClick={() => {
                      setActiveSpeaker(isActive ? null : row.name);
                      onSpeakerPlay?.(row.name);
                    }}
                    title="Клик по имени — solo (только реплики спикера). Клик по времени/тексту — воспроизведение с этой точки."
                  >
                    <td>
                      <span className={cn('transcription-avatar')} style={{ background: color }}>
                        {speakerInitials(row.name)}
                      </span>
                      <span className={cn('transcription-speaker-name')}>{resolved.personName}</span>
                    </td>
                    <td>
                      <SpeakerRoleBadge raw={row.name} />
                    </td>
                    <td>
                      <span className={cn('transcription-lang-badge', {
                        kz: row.lang === 'kz',
                        mixed: row.lang === 'mixed',
                      })}>
                        {langLabel(row.lang)}
                      </span>
                    </td>
                    <td>{row.count}</td>
                    <td>{row.minutes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {activeSpeaker && (
            <button
              type="button"
              className={cn('transcription-clear-filter')}
              onClick={() => setActiveSpeaker(null)}
            >
              Сбросить фильтр: {activeSpeaker}
            </button>
          )}
        </div>
      )}

      <div className={cn('transcription-list')}>
        {filteredItems.map((seg, i) => {
          const base = getSpeakerName(seg);
          const speaker = formatSpeakerWithLang(base, seg.speaker_language);
          const color = speakerColors[speaker] || speakerColors[base] || SPEAKER_COLORS[0];
          const prevBase = filteredItems[i - 1] ? getSpeakerName(filteredItems[i - 1]) : 'Участник';
          const prevSpeaker = formatSpeakerWithLang(prevBase, filteredItems[i - 1]?.speaker_language);
          const showSpeakerHeader = i === 0 || prevSpeaker !== speaker;
          const isKz = seg.speaker_language === 'kz' || !!seg.text_kk?.trim();
          const kkText = (seg.text_kk || (isKz ? seg.text : '') || '').trim();
          const ruText = (seg.text_ru || (!isKz ? seg.text : '') || '').trim();
          const primaryText = isKz ? (kkText || ruText) : (ruText || seg.text || '').trim();
          const showRuTranslation = isKz && kkText && ruText && kkText !== ruText;
          const missingRu = isKz && !ruText && !!kkText;
          const segTags = findTagsForSegment(getSegmentStartSec(seg));
          const startSec = getSegmentStartSec(seg);
          const canSeek = !!onSeekTime;

          return (
            <div key={seg.id ?? i} className={cn('transcription-bubble-row')}>
              {showSpeakerHeader ? (
                <div className={cn('transcription-bubble-header')}>
                  <span className={cn('transcription-avatar')} style={{ background: color }}>
                    {speakerInitials(speaker)}
                  </span>
                  <div>
                    <div className={cn('transcription-bubble-name')} style={{ color }}>
                      {resolveSpeakerRole(base).personName}
                      <SpeakerRoleBadge raw={base} className={cn('transcription-role-badge')} />
                      {seg.detected_language && seg.detected_language !== seg.speaker_language && (
                        <span className={cn('transcription-seg-lang')}>
                          {' '}· {langLabel(seg.detected_language)}
                        </span>
                      )}
                      {seg.speaker_position && (
                        <span className={cn('transcription-bubble-role')}> · {seg.speaker_position}</span>
                      )}
                      {seg.speaker_gender && (
                        <span className={cn('transcription-bubble-role')}> · {seg.speaker_gender}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={cn('transcription-bubble-time', { link: canSeek })}
                      disabled={!canSeek}
                      onClick={() => handleSeek(seg)}
                    >
                      {formatTimeShort(seg.start_time, startSec)}
                    </button>
                  </div>
                </div>
              ) : null}
              <div
                className={cn('transcription-bubble', { grouped: !showSpeakerHeader })}
                style={{ borderLeftColor: color }}
              >
                {!showSpeakerHeader && (
                  <button
                    type="button"
                    className={cn('transcription-bubble-inline-time', { link: canSeek })}
                    disabled={!canSeek}
                    onClick={() => handleSeek(seg)}
                  >
                    {formatTimeShort(seg.start_time, startSec)}
                  </button>
                )}
                {segTags.length > 0 && (
                  <div className={cn('transcription-seg-tags')}>
                    {segTags.map(tag => (
                      <span key={tag} className={cn('transcription-seg-tag')}>{tag}</span>
                    ))}
                  </div>
                )}
                <p
                  className={cn('transcription-text')}
                  dangerouslySetInnerHTML={{
                    __html: highlightSearch(
                      primaryText,
                      activeTerms.length ? activeTerms.join(' ') : search,
                    ),
                  }}
                />
                {missingRu && (
                  <p className={cn('transcription-missing-ru')}>⚠ Нет RU-перевода — дождитесь авто-перевода или нажмите «Перетранскрибировать»</p>
                )}
                {showRuTranslation && (
                  <p className={cn('transcription-text-kk')}>
                    <span className={cn('transcription-kk-label')}>RU:</span>{' '}
                    {ruText}
                  </p>
                )}
              </div>
            </div>
          );
        })}
        {!filteredItems.length && (
          <p className={cn('empty')}>Ничего не найдено. Измените поиск или фильтр.</p>
        )}
      </div>
    </div>
  );
};

export default TranscriptionView;
